import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api, setSessionToken, getStoredSessionToken } from '../lib/api';
import { floorToInt } from '../lib/utils';
import { getDeviceBindingPayload } from '../lib/deviceBinding';
import { getGoogleLoginTokenFromLocation, isAuthRejection } from '../lib/sessionBootstrap';

const AuthContext = createContext();

const getISTDateKey = () => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const getPart = (type) => parts.find((part) => part.type === type)?.value;
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const hour = Number(getPart('hour') || '0');

    if (!year || !month || !day) {
      return new Date().toISOString().slice(0, 10);
    }

    const currentDateString = `${year}-${month}-${day}`;
    if (hour >= 19) {
      return currentDateString;
    }

    const baseDate = new Date(`${currentDateString}T00:00:00Z`);
    baseDate.setUTCDate(baseDate.getUTCDate() - 1);
    return baseDate.toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

const getReferrerFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref');
  } catch {
    return null;
  }
};

const getAuthCallbackUserFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const email = params.get('email') || params.get('user_email');
    if (!email) return null;

    return {
      email,
      name: params.get('name') || params.get('user_name') || undefined,
      picture: params.get('picture') || params.get('avatar') || params.get('profilePic') || undefined,
    };
  } catch {
    return null;
  }
};

// Detect our own one-time Google-login handoff token when the OAuth callback
// bounces the browser back into the SPA on boot.
//
// The browser web flow delivers this token in the URL *hash*
// (`/login#google_login_token=<token>`), and the APK flow delivers it in the
// *query* of the dedicated `/app-login-complete?token=<token>` route — which is
// consumed by the AppLoginComplete component, not here. Our backend never puts
// a login token in the query of any other route.
//
// Therefore the bootstrap must key off the hash ONLY. Reading a bare `?token=`
// (or `?google_login_token=`) from the query of an arbitrary route is unsafe:
// the sponsor/link-shortener flow returns the user to `/free-puzzle` with
// third-party tracking params appended (e.g. `?token=…`), and mistaking one of
// those for a Google login token triggers a failed exchange whose error handler
// wipes the session — logging the user out exactly at the end of the puzzle
// unlock journey. Keying off the hash (which no shortener appends) avoids it.
// Decode a Google ID token (JWT) payload WITHOUT verifying it. This is only
// used to read the email/name/picture so we can send `identifier` alongside the
// token; the backend independently verifies the token's signature/audience/
// issuer/expiry and asserts the email matches. Returns null if it can't parse.
const decodeGoogleIdToken = (idToken) => {
  try {
    const parts = String(idToken).split('.');
    if (parts.length < 2) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(json);
    if (!payload?.email) return null;
    return { email: payload.email, name: payload.name, picture: payload.picture };
  } catch {
    return null;
  }
};

// Decode our own session JWT payload WITHOUT verifying it — only used to read
// the user id (`sub`) and expiry (`exp`) locally so the app knows whether the
// stored token is even worth sending. The backend independently verifies the
// signature and expiry on every request; this is never treated as proof of
// anything by itself.
const decodeSessionToken = (token) => {
  try {
    const parts = String(token).split('.');
    if (parts.length < 2) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    const payload = JSON.parse(json);
    if (!payload?.sub) return null;
    return { userId: payload.sub, exp: Number(payload.exp || 0) };
  } catch {
    return null;
  }
};

// A restore/refresh request failed. Decide whether the failure actually proves
// the stored session is invalid — i.e. the backend returned 401 and rejected
// the bearer token (expired / tampered / mismatched) — versus a transient
// failure (a timeout, a dropped/flaky mobile connection, or a 5xx) that says
// nothing about the token. Only a genuine 401 should ever clear the session.
// Returning from the external sponsor/ad redirect chain — especially on a cold
// WebView reload on mobile data — very frequently makes the first request fail
// transiently, and treating that as "logged out" is the regression this guards.
const FREE_PUZZLE_WINNER_SKIP_KEY = 'hardaySkipWinnerModalAfterFreePuzzle';
const FREE_PUZZLE_WINNER_SKIP_TTL_MS = 15 * 60 * 1000;

const readFreePuzzleWinnerSkip = () => {
  try {
    const raw = sessionStorage.getItem(FREE_PUZZLE_WINNER_SKIP_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const expiresAt = Number(parsed?.expiresAt || 0);
    if (!expiresAt || Date.now() > expiresAt) {
      sessionStorage.removeItem(FREE_PUZZLE_WINNER_SKIP_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

const writeFreePuzzleWinnerSkip = (winnerId = null) => {
  const skip = {
    winnerId: winnerId || null,
    expiresAt: Date.now() + FREE_PUZZLE_WINNER_SKIP_TTL_MS,
  };

  try {
    sessionStorage.setItem(FREE_PUZZLE_WINNER_SKIP_KEY, JSON.stringify(skip));
  } catch {
    // Storage may be unavailable in private/webview contexts; the in-memory
    // skip state below still protects the active post-puzzle return flow.
  }

  return skip;
};



export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('english');
  const [hasSolvedToday, setHasSolvedToday] = useState(false);
  const [referrerCode, setReferrerCode] = useState(null);
  const [poolTotal, setPoolTotal] = useState(0);
  const [nextDrawAt, setNextDrawAt] = useState(null);
  const [previousWinners, setPreviousWinners] = useState([]);
  const [winnerAnnouncement, setWinnerAnnouncement] = useState(null);
  const [isWinnerPopupOpen, setIsWinnerPopupOpen] = useState(false);
  const [sessionSeenWinnerIds, setSessionSeenWinnerIds] = useState([]);
  const [freePuzzleSkippedWinnerIds, setFreePuzzleSkippedWinnerIds] = useState([]);
  const [skipWinnerModalAfterFreePuzzleUntil, setSkipWinnerModalAfterFreePuzzleUntil] = useState(() => Number(readFreePuzzleWinnerSkip()?.expiresAt || 0));
  const [adminConfig, setAdminConfig] = useState({
    poolPerc: 100,
    addToPool: 0,
    autoAddDaily: false,
    adminUsername: '@rahul_s',
    referralIncentivePerc: 10,
    referralTarget: 100,
    referralNextTarget: 200,
    freePuzzleSponsorLink: '',
    freePuzzleSponsorLinks: ['', '', ''],
  });
  const [adminWithdrawals, setAdminWithdrawals] = useState([]);
  const [withdrawalStats, setWithdrawalStats] = useState({
    totalRequests: 0,
    totalPending: 0,
    totalPendingAmount: 0,
    totalAmountGivenAllTime: 0,
    totalAmountGivenYesterday: 0,
    totalAmountGivenLastWeek: 0,
  });
  const [adminTopupRequests, setAdminTopupRequests] = useState([]);
  const [topupRequestStats, setTopupRequestStats] = useState({
    totalRequests: 0,
    totalPending: 0,
    totalPendingAmount: 0,
    totalApproved: 0,
    totalApprovedAmount: 0,
    totalApprovedAmountToday: 0,
    totalRejected: 0,
  });
  const [topupUpiIds, setTopupUpiIds] = useState(['HARDEEPSWAICH29@YBL']);
  const [topupPayeeName, setTopupPayeeName] = useState('Harday');

  const persistUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('hardayUser', JSON.stringify(updatedUser));
  }, []);

  // Persists both the user object (for instant UI hydration) and the signed
  // session token (for auth on every subsequent request) after a successful
  // login/migration. Call this instead of persistUser directly whenever a
  // `token` is available in the response.
  const persistSession = useCallback((updatedUser, token) => {
    persistUser(updatedUser);
    if (token) {
      setSessionToken(token);
    }
  }, [persistUser]);


  useEffect(() => {
    if (!user?.id || typeof window === 'undefined') return undefined;
    const registerToken = (token) => {
      if (!token || !window.HardayAndroid?.registerPushToken) return;
      window.HardayAndroid.registerPushToken(user.id, token);
    };
    const handler = (event) => registerToken(event?.detail);
    window.addEventListener('harday-fcm-token', handler);
    if (window.HardayAndroid && window.__hardayFcmToken) {
      registerToken(window.__hardayFcmToken);
    }
    return () => window.removeEventListener('harday-fcm-token', handler);
  }, [user?.id]);

  const markFreePuzzleWinnerModalSkip = useCallback((winnerId = null) => {
    const skip = writeFreePuzzleWinnerSkip(winnerId);
    setSkipWinnerModalAfterFreePuzzleUntil(skip.expiresAt);
    return skip;
  }, []);

  const openWinnerAnnouncementIfNeeded = useCallback((announcement) => {
    if (!announcement?.id) return;
    if (sessionSeenWinnerIds.includes(announcement.id)) return;
    if (freePuzzleSkippedWinnerIds.includes(announcement.id)) return;

    const pendingFreePuzzleSkip = readFreePuzzleWinnerSkip();
    const isFreePuzzleRoute = window.location.pathname === '/free-puzzle';
    const hasActiveFreePuzzleSkip = skipWinnerModalAfterFreePuzzleUntil > Date.now();
    if (hasActiveFreePuzzleSkip || pendingFreePuzzleSkip || isFreePuzzleRoute) {
      // The user has just returned from the sponsor/free-puzzle flow; do not let
      // the missed-result modal interrupt the dashboard landing. This is a
      // client-side presentation skip only, not a permanent seen marker.
      console.info('winner modal skipped because user returned from free puzzle');
      const skip = pendingFreePuzzleSkip?.winnerId ? pendingFreePuzzleSkip : markFreePuzzleWinnerModalSkip(announcement.id);
      setSkipWinnerModalAfterFreePuzzleUntil(Number(skip?.expiresAt || 0));
      setFreePuzzleSkippedWinnerIds((prev) => (prev.includes(announcement.id) ? prev : [...prev, announcement.id]));
      return;
    }

    console.info('winner modal shown on normal website entry');
    setWinnerAnnouncement(announcement);
    setIsWinnerPopupOpen(true);
  }, [freePuzzleSkippedWinnerIds, markFreePuzzleWinnerModalSkip, sessionSeenWinnerIds, skipWinnerModalAfterFreePuzzleUntil]);

  const refreshPoolStatus = useCallback(async () => {
    try {
      const { data } = await api.getPoolStatus();
      setPoolTotal(floorToInt(data?.poolTotal ?? 0));
      setNextDrawAt(data?.nextDrawAt || null);
      localStorage.setItem('hardayPool', String(floorToInt(data?.poolTotal ?? 0)));
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, []);

  const refreshWinners = useCallback(async () => {
    try {
      const { data } = await api.getWinners(10);
      setPreviousWinners(Array.isArray(data?.winners) ? data.winners : []);
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, []);

  const refreshAdminConfig = useCallback(async () => {
    try {
      const { data } = await api.getAdminConfig();
      const cfg = data?.config || {};
      setAdminConfig((prev) => ({ ...prev, ...cfg }));
      return { success: true, config: cfg };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, []);

  const refreshAdminWithdrawals = useCallback(async (filters = {}) => {
    try {
      const { data } = await api.getAdminWithdrawals(filters);
      const withdrawals = Array.isArray(data?.withdrawals) ? data.withdrawals : [];
      const stats = data?.stats || {};
      setAdminWithdrawals(withdrawals);
      setWithdrawalStats((prev) => ({
        ...prev,
        ...stats,
      }));
      return { success: true, withdrawals, stats };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, []);

  const refreshAdminTopupRequests = useCallback(async (filters = {}) => {
    try {
      const { data } = await api.getAdminTopupRequests(filters);
      const requests = Array.isArray(data?.requests) ? data.requests : [];
      const stats = data?.stats || {};
      const upiIds = Array.isArray(data?.upiIds) && data.upiIds.length > 0
        ? data.upiIds
        : ['HARDEEPSWAICH29@YBL'];
      const payeeName = data?.payeeName || 'Harday';
      setAdminTopupRequests(requests);
      setTopupRequestStats((prev) => ({ ...prev, ...stats }));
      setTopupUpiIds(upiIds);
      setTopupPayeeName(payeeName);
      return { success: true, requests, stats, upiIds, payeeName };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, []);

  const updateAdminTopupRequestStatus = async (requestId, payload, filters = {}) => {
    try {
      const { data } = await api.updateTopupRequestStatus(requestId, payload);
      await refreshAdminTopupRequests(filters);
      // If we approved, the user's wallet may have changed (especially if it's the current user)
      if (payload?.action === 'approve' && data?.applied?.user) {
        const approvedUser = data.applied.user;
        if (approvedUser?.id && user?.id && approvedUser.id === user.id) {
          persistUser(approvedUser);
        }
      }
      return { success: true, request: data?.request };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const updateAdminTopupUpiIds = async (upiIds, payeeName) => {
    try {
      const payload = { upiIds };
      if (payeeName !== undefined && payeeName !== null) {
        payload.payeeName = payeeName;
      }
      const { data } = await api.updateTopupUpiIds(payload);
      const nextIds = Array.isArray(data?.upiIds) && data.upiIds.length > 0
        ? data.upiIds
        : ['HARDEEPSWAICH29@YBL'];
      setTopupUpiIds(nextIds);
      if (data?.payeeName) setTopupPayeeName(data.payeeName);
      return { success: true, upiIds: nextIds, payeeName: data?.payeeName };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const completeGoogleLoginToken = useCallback(async (googleLoginToken) => {
    try {
      const deviceBinding = await getDeviceBindingPayload();
      const { data } = await api.exchangeGoogleLoginToken(googleLoginToken, deviceBinding);
      const loggedInUser = data?.user;
      if (!loggedInUser) {
        return { success: false, error: 'Google login did not return a user' };
      }

      persistSession(loggedInUser, data?.token);
      setIsAuthenticated(true);
      setHasSolvedToday((Number(loggedInUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || loggedInUser?.lastPuzzleSolvedDate === getISTDateKey());
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }
      if (data?.nextPath) {
        sessionStorage.setItem('hardayPostLoginPath', data.nextPath);
      }
      localStorage.removeItem('hardayReferrer');
      return { success: true, data };
    } catch (error) {
      localStorage.removeItem('hardayUser');
      setSessionToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setHasSolvedToday(false);
      return { success: false, error: error?.response?.data?.detail || error.message || 'Google login failed' };
    }
  }, [persistSession, openWinnerAnnouncementIfNeeded]);

  const refreshCurrentUser = useCallback(async () => {
    if (!user?.id) {
      return { success: false, error: 'No user' };
    }

    try {
      const { data } = await api.getUser(user.id);
      const refreshedUser = data?.user;
      // Sliding session: the backend hands back a fresh token when the current
      // one is aging. Persist it so the user stays logged in until they log out.
      if (data?.token) {
        setSessionToken(data.token);
      }
      if (refreshedUser) {
        persistUser(refreshedUser);
        setHasSolvedToday((Number(refreshedUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || refreshedUser?.lastPuzzleSolvedDate === getISTDateKey());
      }

      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  }, [user, persistUser, openWinnerAnnouncementIfNeeded]);

  useEffect(() => {
    const bootstrap = async () => {
      const storedUserRaw = localStorage.getItem('hardayUser');

      const urlRef = getReferrerFromUrl();
      if (urlRef) {
        localStorage.setItem('hardayReferrer', urlRef);
        setReferrerCode(urlRef);
      }

      const storedRef = localStorage.getItem('hardayReferrer');
      if (storedRef) {
        setReferrerCode(storedRef);
      }

      const googleLoginToken = getGoogleLoginTokenFromLocation(window.location);

      await Promise.all([refreshPoolStatus(), refreshWinners(), refreshAdminConfig()]);

      let restoredSession = false;

      if (googleLoginToken) {
        const result = await completeGoogleLoginToken(googleLoginToken);
        restoredSession = result.success;
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      if (!restoredSession) {
        // Preferred path: a signed session token from a previous login. Check
        // its (unverified, local-only) expiry first so an obviously-dead
        // token doesn't cost a round trip; the backend independently verifies
        // the signature and expiry on the actual request no matter what.
        const storedToken = getStoredSessionToken();
        const decodedToken = storedToken ? decodeSessionToken(storedToken) : null;
        const tokenLooksLive = Boolean(decodedToken && decodedToken.exp * 1000 > Date.now());

        if (storedToken && tokenLooksLive) {
          try {
            const { data } = await api.getUser(decodedToken.userId);
            const refreshedUser = data?.user;
            if (refreshedUser) {
              // Prefer a backend-refreshed token (sliding session) over the one
              // we sent, so long-lived app installs keep a valid token.
              persistSession(refreshedUser, data?.token || storedToken);
              setIsAuthenticated(true);
              setHasSolvedToday((Number(refreshedUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || refreshedUser?.lastPuzzleSolvedDate === getISTDateKey());
              restoredSession = true;
            }
            if (data?.winnerAnnouncement) {
              openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
            }
          } catch (error) {
            if (isAuthRejection(error)) {
              // Backend actively rejected the token (expired/invalid/tampered).
              // Only now is it safe to drop it and fall through to the
              // legacy-upgrade check below, then to a normal login screen.
              setSessionToken(null);
            } else if (storedUserRaw) {
              // Transient failure (timeout / flaky mobile network / 5xx),
              // common on the cold reload right after returning from the
              // external sponsor/ad redirect chain — especially inside the
              // Android WebView. The token is almost certainly still valid, so
              // KEEP it and optimistically restore the session from the cached
              // user object instead of logging the user out. The periodic
              // refreshCurrentUser poll reconciles real data (and refreshes the
              // token) once the network recovers.
              try {
                const cachedUser = JSON.parse(storedUserRaw);
                if (cachedUser?.id) {
                  setUser(cachedUser);
                  setIsAuthenticated(true);
                  setHasSolvedToday((Number(cachedUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || cachedUser?.lastPuzzleSolvedDate === getISTDateKey());
                  restoredSession = true;
                }
              } catch {
                // Cached user unreadable; leave the token in place and fall
                // through without forcing a logout.
              }
            }
          }
        } else if (storedToken) {
          setSessionToken(null);
        }

        // Transitional: a client that logged in before this change only has
        // the raw user object in localStorage, no signed token. Exchange it
        // for a real session token exactly once so these existing users are
        // not force-logged-out during rollout; every request after this uses
        // the token like everyone else.
        if (!restoredSession && storedUserRaw) {
          try {
            const parsedUser = JSON.parse(storedUserRaw);
            const { data } = await api.migrateLegacySession(parsedUser.id);
            const migratedUser = data?.user;
            if (migratedUser && data?.token) {
              persistSession(migratedUser, data.token);
              setIsAuthenticated(true);
              setHasSolvedToday((Number(migratedUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || migratedUser?.lastPuzzleSolvedDate === getISTDateKey());
              restoredSession = true;
            }
            if (data?.winnerAnnouncement) {
              openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
            }
          } catch (error) {
            if (isAuthRejection(error)) {
              // Genuine rejection (e.g. the legacy session was already migrated
              // on another device) — clear the stale raw user object and send
              // them to a fresh login.
              localStorage.removeItem('hardayUser');
              setSessionToken(null);
              setUser(null);
              setIsAuthenticated(false);
              setHasSolvedToday(false);
            }
            // Otherwise a transient/network failure: keep the cached user object
            // so a later launch can retry the migration, instead of force-
            // logging-out on a flaky network right after the ad redirect chain.
          }
        }
      }

      if (!restoredSession) {
        const callbackUser = getAuthCallbackUserFromUrl();

        if (callbackUser?.email) {
          try {
            const referredBy = urlRef || storedRef || localStorage.getItem('hardayReferrer') || null;
            const deviceBinding = await getDeviceBindingPayload();
            const { data } = await api.login({
              identifier: callbackUser.email,
              name: callbackUser.name,
              profilePic: callbackUser.picture,
              referredBy,
              ...deviceBinding,
            });

            const loggedInUser = data?.user;
            if (loggedInUser) {
              persistSession(loggedInUser, data?.token);
              setIsAuthenticated(true);
              setHasSolvedToday((Number(loggedInUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || loggedInUser?.lastPuzzleSolvedDate === getISTDateKey());
              if (data?.winnerAnnouncement) {
                openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
              }
              localStorage.removeItem('hardayReferrer');
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } catch {
            // no-op: fallback to login screen
          }
        }
      }

      setLoading(false);
    };

    bootstrap();
  }, [completeGoogleLoginToken, persistUser, persistSession, refreshPoolStatus, refreshWinners, refreshAdminConfig, openWinnerAnnouncementIfNeeded]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshAdminConfig();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshAdminConfig]);


  useEffect(() => {
    if (!user) {
      setHasSolvedToday(false);
      return;
    }
    setHasSolvedToday((Number(user?.dailyPuzzleEntriesCompleted || 0) >= 3) || user?.lastPuzzleSolvedDate === getISTDateKey());
  }, [user]);


  useEffect(() => {
    const interval = setInterval(() => {
      refreshPoolStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshPoolStatus]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshWinners();
    }, 20000);
    return () => clearInterval(interval);
  }, [refreshWinners]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    refreshCurrentUser();
    api.heartbeat(user.id).catch(() => {});

    const refreshInterval = setInterval(() => {
      refreshCurrentUser();
    }, 7000);

    const heartbeatInterval = setInterval(() => {
      api.heartbeat(user.id).catch(() => {});
    }, 20000);

    // Immediately pull fresh user data (credits, profile score, stats, etc.)
    // whenever the tab regains focus or becomes visible again. This ensures
    // admin-side changes are reflected right away when the user switches back
    // to the website tab, without waiting for the 7-second poll.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshCurrentUser();
      }
    };
    const handleFocus = () => {
      refreshCurrentUser();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(refreshInterval);
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, user?.id, refreshCurrentUser]);

  const loginWithGoogle = async (googleUser) => {
    try {
      const identifier = googleUser?.email;
      if (!identifier) {
        return { success: false, error: 'Missing Google email' };
      }

      const referredBy = referrerCode || localStorage.getItem('hardayReferrer') || null;
      const deviceBinding = await getDeviceBindingPayload();
      const { data } = await api.login({
        identifier,
        name: googleUser?.name,
        profilePic: googleUser?.picture,
        referredBy,
        ...deviceBinding,
      });

      const loggedInUser = data?.user;
      if (!loggedInUser) {
        return { success: false, error: 'Login failed' };
      }

      persistSession(loggedInUser, data?.token);
      setIsAuthenticated(true);
      setHasSolvedToday((Number(loggedInUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || loggedInUser?.lastPuzzleSolvedDate === getISTDateKey());
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }

      await Promise.all([
        refreshPoolStatus(),
        refreshWinners(),
        refreshAdminConfig(),
      ]);
      localStorage.removeItem('hardayReferrer');
      return { success: true, isNew: Boolean(loggedInUser?.isFirstVisit) };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  // Native Android Google Sign-In completion (Bridge approach). The APK obtains
  // a Google ID token via Credential Manager and hands it to us; we complete
  // login through the SAME `/users/login` path the website uses, adding the
  // token for backend verification. Email anchors identity, so existing users
  // log into the same account and user IDs are preserved. Referrals and device
  // fingerprinting are carried through unchanged.
  const loginWithGoogleIdToken = async (idToken) => {
    try {
      const decoded = decodeGoogleIdToken(idToken);
      if (!decoded?.email) {
        return { success: false, error: 'Could not read Google account from sign-in.' };
      }

      const referredBy = referrerCode || localStorage.getItem('hardayReferrer') || null;
      const deviceBinding = await getDeviceBindingPayload();
      const { data } = await api.login({
        identifier: decoded.email,
        name: decoded.name,
        profilePic: decoded.picture,
        googleIdToken: idToken,
        referredBy,
        ...deviceBinding,
      });

      const loggedInUser = data?.user;
      if (!loggedInUser) {
        return { success: false, error: 'Login failed' };
      }

      persistSession(loggedInUser, data?.token);
      setIsAuthenticated(true);
      setHasSolvedToday((Number(loggedInUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || loggedInUser?.lastPuzzleSolvedDate === getISTDateKey());
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }

      await Promise.all([
        refreshPoolStatus(),
        refreshWinners(),
        refreshAdminConfig(),
      ]);
      localStorage.removeItem('hardayReferrer');
      return { success: true, isNew: Boolean(loggedInUser?.isFirstVisit) };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const login = async (identifier) => {
    try {
      const referredBy = referrerCode || localStorage.getItem('hardayReferrer') || null;
      const deviceBinding = await getDeviceBindingPayload();
      const { data } = await api.login({ identifier, referredBy, ...deviceBinding });
      const loggedInUser = data?.user;
      if (!loggedInUser) {
        return { success: false, error: 'Invalid credentials' };
      }

      persistSession(loggedInUser, data?.token);
      setIsAuthenticated(true);
      setHasSolvedToday((Number(loggedInUser?.dailyPuzzleEntriesCompleted || 0) >= 3) || loggedInUser?.lastPuzzleSolvedDate === getISTDateKey());
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }
      await Promise.all([
        refreshPoolStatus(),
        refreshWinners(),
        refreshAdminConfig(),
      ]);
      localStorage.removeItem('hardayReferrer');
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || 'Invalid credentials' };
    }
  };

  const requestWithdrawal = async (payload) => {
    if (!user?.id) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const { data } = await api.createWithdrawal(user.id, payload);
      const updatedUser = data?.user;
      const withdrawal = data?.withdrawal;
      const alreadyPending = Boolean(data?.alreadyPending);

      if (updatedUser) {
        persistUser(updatedUser);
      }

      return { success: true, withdrawal, user: updatedUser, alreadyPending };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setHasSolvedToday(false);
    setWinnerAnnouncement(null);
    setIsWinnerPopupOpen(false);
    setSessionSeenWinnerIds([]);
    setFreePuzzleSkippedWinnerIds([]);
    setSkipWinnerModalAfterFreePuzzleUntil(0);
    setAdminWithdrawals([]);
    setWithdrawalStats({
      totalRequests: 0,
      totalPending: 0,
      totalPendingAmount: 0,
      totalAmountGivenAllTime: 0,
      totalAmountGivenYesterday: 0,
      totalAmountGivenLastWeek: 0,
    });
    localStorage.removeItem('hardayUser');
    localStorage.removeItem('hardaySolvedDate');
    localStorage.removeItem('hardayLastDeduction');
    setSessionToken(null);
    sessionStorage.removeItem(FREE_PUZZLE_WINNER_SKIP_KEY);
  };

  const deleteAccount = async () => {
    if (!user?.id) {
      return { success: false, error: 'Not signed in.' };
    }
    try {
      await api.deleteOwnAccount(user.id);
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.detail || error.message || 'Failed to delete account.',
      };
    }
    // Clear ALL local session/auth data.
    try {
      localStorage.removeItem('hardayUser');
      localStorage.removeItem('hardaySolvedDate');
      localStorage.removeItem('hardayLastDeduction');
      sessionStorage.removeItem(FREE_PUZZLE_WINNER_SKIP_KEY);
      localStorage.removeItem('hardayRedeemUpiId');
    } catch (err) {
      // ignore storage errors
    }
    logout();
    return { success: true };
  };

  const updateCredits = async (amount) => {
    if (!user) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const { data } = await api.topup(user.id, Number(amount));
      const updatedUser = data?.user;
      if (updatedUser) {
        persistUser(updatedUser);
      }
      if (data?.pool?.poolTotal !== undefined) {
        setPoolTotal(floorToInt(data.pool.poolTotal));
        setNextDrawAt(data.pool.nextDrawAt || null);
      }
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const applyTopupResult = (data) => {
    const updatedUser = data?.user;
    if (updatedUser) {
      persistUser(updatedUser);
    }
    if (data?.pool?.poolTotal !== undefined) {
      setPoolTotal(floorToInt(data.pool.poolTotal));
      setNextDrawAt(data.pool.nextDrawAt || null);
    }
    if (data?.winnerAnnouncement) {
      openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
    }
  };

  const solvePuzzle = async (selectedOption) => {
    if (!user) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const { data } = await api.solvePuzzle(user.id, { selectedOption });
      const updatedUser = data?.user;
      if (updatedUser) {
        persistUser(updatedUser);
        setHasSolvedToday((updatedUser?.dailyPuzzleEntriesCompleted || 0) >= 3 || updatedUser?.lastPuzzleSolvedDate === getISTDateKey());
      }
      if (data?.pool?.poolTotal !== undefined) {
        setPoolTotal(floorToInt(data.pool.poolTotal));
        setNextDrawAt(data.pool.nextDrawAt || null);
      }
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }
      return { success: true, isCorrect: Boolean(data?.isCorrect), result: data?.result || 'incorrect' };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const solveFreePuzzle = async (selectedOption, entryNumber) => {
    if (!user) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      console.info('HARDAY_PUZZLE_SUBMIT_RECEIVED');
      const { data } = await api.submitFreePuzzle(user.id, { selectedOption, entryNumber });
      console.info('HARDAY_PUZZLE_ENTRY_COMPLETED');
      const updatedUser = data?.user;
      if (updatedUser) {
        persistUser(updatedUser);
        setHasSolvedToday((updatedUser?.dailyPuzzleEntriesCompleted || 0) >= 3 || updatedUser?.lastPuzzleSolvedDate === getISTDateKey());
      }
      if (data?.pool?.poolTotal !== undefined) {
        setPoolTotal(floorToInt(data.pool.poolTotal));
        setNextDrawAt(data.pool.nextDrawAt || null);
      } else if (data?.liveCount !== undefined) {
        setPoolTotal(floorToInt(data.liveCount));
      }
      console.info('HARDAY_LIVE_COUNT_INCREMENTED');
      console.info('HARDAY_NEXT_PUZZLE_ADVANCED');
      await refreshPoolStatus();
      markFreePuzzleWinnerModalSkip(data?.winnerAnnouncement?.id || null);
      if (data?.winnerAnnouncement) {
        openWinnerAnnouncementIfNeeded(data.winnerAnnouncement);
      }
      return { success: true, isCorrect: Boolean(data?.isCorrect), result: data?.result || 'incorrect', fromFreePuzzle: true, skipWinnerModal: true };
    } catch (error) {
      console.info('HARDAY_NEXT_PUZZLE_BLOCKED_NOT_SUBMITTED', error?.response?.data?.detail || error.message);
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const updateAdminWithdrawalStatus = async (withdrawalId, payload, filters = {}) => {
    try {
      const { data } = await api.updateWithdrawalStatus(withdrawalId, payload);
      await refreshAdminWithdrawals(filters);
      return { success: true, withdrawal: data?.withdrawal };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const updateNotificationSettings = async (payload) => {
    if (!user) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const { data } = await api.updateNotificationSettings(user.id, payload);
      const updatedUser = data?.user;
      if (updatedUser) {
        persistUser(updatedUser);
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  // Mark a SINGLE winner of the current announcement seen (no popup close). On a
  // two-winner day the popup calls this once per winner as the user advances
  // through the two modals.
  const markWinnerAnnouncementSeenById = async (winnerId) => {
    if (!winnerId || !user?.id) return;
    try {
      await api.markWinnerAnnouncementSeen(user.id, winnerId);
    } catch {
      // no-op: UI can still advance; backend will resend if truly unseen
    }
    setSessionSeenWinnerIds((prev) => (prev.includes(winnerId) ? prev : [...prev, winnerId]));
  };

  // Close the popup and sync user state. The popup marks each winner seen as it
  // goes, so by the time this runs every shown winner is already acknowledged.
  const closeWinnerAnnouncement = () => {
    setIsWinnerPopupOpen(false);
    setWinnerAnnouncement(null);
    refreshCurrentUser();
  };

  // Close-all fallback (e.g. programmatic dismiss): mark every winner of the
  // current announcement seen, then close.
  const dismissWinnerAnnouncement = async () => {
    const list = winnerAnnouncement?.winners?.length
      ? winnerAnnouncement.winners
      : (winnerAnnouncement ? [winnerAnnouncement] : []);
    for (const w of list) {
      // eslint-disable-next-line no-await-in-loop
      await markWinnerAnnouncementSeenById(w?.id);
    }
    closeWinnerAnnouncement();
  };

  const updateAdminPoolConfig = async (payload) => {
    try {
      const { data } = await api.updateAdminConfig(payload);
      const cfg = data?.config || {};
      setAdminConfig((prev) => ({ ...prev, ...cfg }));
      await refreshPoolStatus();
      return { success: true, config: cfg, notificationsSent: data?.notificationsSent ?? 0 };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const manualAddToPool = async (amount) => {
    try {
      const { data } = await api.manualAddToPool(Number(amount));
      if (data?.pool?.poolTotal !== undefined) {
        setPoolTotal(floorToInt(data.pool.poolTotal));
        setNextDrawAt(data.pool.nextDrawAt || null);
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  };

  const getReferralLink = () => {
    if (!user) return '';
    return `${window.location.origin}/login?ref=${user.id}`;
  };

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return user.username === (adminConfig?.adminUsername || '@rahul_s');
  }, [user, adminConfig]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithGoogle,
    loginWithGoogleIdToken,
    logout,
    deleteAccount,
    language,
    setLanguage,
    hasSolvedToday,
    setHasSolvedToday,
    updateCredits,
    applyTopupResult,
    solvePuzzle,
    solveFreePuzzle,
    requestWithdrawal,
    updateNotificationSettings,
    poolTotal,
    nextDrawAt,
    previousWinners,
    winnerAnnouncement,
    isWinnerPopupOpen,
    dismissWinnerAnnouncement,
    markWinnerAnnouncementSeenById,
    closeWinnerAnnouncement,
    markFreePuzzleWinnerModalSkip,
    getReferralLink,
    setReferrer: setReferrerCode,
    isAdmin,
    adminConfig,
    refreshAdminConfig,
    updateAdminPoolConfig,
    manualAddToPool,
    adminWithdrawals,
    withdrawalStats,
    refreshAdminWithdrawals,
    updateAdminWithdrawalStatus,
    adminTopupRequests,
    topupRequestStats,
    topupUpiIds,
    topupPayeeName,
    refreshAdminTopupRequests,
    updateAdminTopupRequestStatus,
    updateAdminTopupUpiIds,
    refreshPoolStatus,
    refreshCurrentUser,
    completeGoogleLoginToken,
    persistUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
